import { alpha } from "@mui/material/styles";
import { BRAND, BRAND_GRADIENT } from "../components/MobileList";

// Global look of MUI components. Change a component's style here and every
// page picks it up; per-page `sx` still wins when a page needs something else.

const border = "#eaecf0";
const text = "#101828";
const textMuted = "#475467";

const componentOverrides = {
  MuiTypography: {
    defaultProps: {
      fontFamily: "Cairo",
    },
  },

  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        border: `1px solid ${border}`,
        boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
      },
    },
  },

  MuiCardHeader: {
    styleOverrides: {
      root: {
        padding: "16px 20px",
        "&:not(:last-child)": { borderBottom: `1px solid ${border}` },
      },
      title: { fontSize: 16, fontWeight: 700, color: text },
      subheader: { fontSize: 12.5, color: "#667085", marginTop: 2 },
    },
  },

  MuiCardContent: {
    styleOverrides: {
      root: { padding: 20 },
    },
  },

  // Dialogs: rounded paper and a brand header (DialogTitle). The grey body and
  // footer of add/edit dialogs live in FormDialog, so date pickers and other
  // plain dialogs keep MUI's defaults.
  MuiDialog: {
    styleOverrides: {
      paper: { borderRadius: 16 },
      paperFullScreen: { borderRadius: 0 },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        background: BRAND_GRADIENT,
        color: "#fff",
        padding: "18px 24px",
        fontSize: 18,
        fontWeight: 700,
        // titles passed as <Typography color="..."> stay readable
        "& .MuiTypography-root": { color: "inherit" },
      },
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: 12,
        border: `1px solid ${border}`,
        boxShadow: "0 12px 24px -4px rgba(16,24,40,0.12)",
      },
    },
  },

  MuiAutocomplete: {
    styleOverrides: {
      paper: {
        borderRadius: 12,
        border: `1px solid ${border}`,
        boxShadow: "0 12px 24px -4px rgba(16,24,40,0.12)",
      },
      option: {
        borderRadius: 8,
        margin: "0 6px",
      },
    },
  },

  // Text fields, selects and autocompletes all render an OutlinedInput.
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 10,
        backgroundColor: "#f9fafb",
        transition: "background-color 0.15s",
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d0d5dd" },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#98a2b3" },
        "&.Mui-focused": {
          backgroundColor: "#fff",
          boxShadow: `0 0 0 4px ${alpha(BRAND, 0.1)}`,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: BRAND,
          borderWidth: 1,
        },
      },
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      root: { color: "#667085" },
    },
  },

  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        borderRadius: 10,
        fontWeight: 600,
        textTransform: "none",
      },
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: { borderRadius: 10 },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: { borderRadius: 8, fontWeight: 600 },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        borderRadius: 8,
        backgroundColor: text,
        fontSize: 12,
        padding: "6px 10px",
      },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: { textTransform: "none", fontWeight: 600 },
    },
  },

  MuiTabs: {
    styleOverrides: {
      indicator: { height: 3, borderRadius: 3 },
    },
  },

  MuiPaginationItem: {
    styleOverrides: {
      root: { borderRadius: 8 },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: { borderColor: border },
      head: { backgroundColor: "#f9fafb", color: textMuted, fontWeight: 700 },
    },
  },

  MuiDataGrid: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        border: `1px solid ${border}`,
        backgroundColor: "#fff",
        color: text,
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: "#f9fafb",
          borderBottom: `1px solid ${border}`,
          borderRadius: "12px 12px 0 0",
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          fontWeight: 700,
          color: textMuted,
        },
        "& .MuiDataGrid-columnSeparator": { color: border },
        "& .MuiDataGrid-cell": { borderBottom: `1px solid #f2f4f7` },
        "& .MuiDataGrid-row:hover": {
          backgroundColor: alpha(BRAND, 0.04),
        },
        "& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover":
          { backgroundColor: alpha(BRAND, 0.08) },
        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
          { outline: "none" },
        "& .MuiDataGrid-footerContainer": {
          borderTop: `1px solid ${border}`,
        },
      },
    },
  },
};

export default componentOverrides;
